import { Link } from "react-router-dom";
import { Phone, Mail } from "lucide-react";
import { BUSINESS } from "@/data/content";
import { CITIES, CITY_ORDER } from "@/data/cities";
import { PANEL_CITIES, PANEL_CITY_ORDER } from "@/data/panelUpgradeCities";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-[#262626] bg-[#0A0A0A] pt-20 pb-8"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Link to="/">
              <img
                src="/electromax-logo-dark.png"
                alt="ElectroMax Systems"
                className="h-32 md:h-40 w-auto object-contain"
                draggable="false"
              />
            </Link>
            <p className="mt-6 text-sm text-white/60 max-w-sm leading-relaxed">
              Premium electrical services for residential and commercial clients across Metro Vancouver and the Fraser Valley.
            </p>
            <div className="mt-6 text-[10px] tracking-[0.24em] uppercase text-white/40">
              {BUSINESS.license}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-4">
              Explore
            </div>
            <ul className="space-y-2 text-sm">
              {[
                { l: "Services", h: "/#services" },
                { l: "Projects", h: "/projects" },
                { l: "Blog", h: "/blog" },
                { l: "About", h: "/about" },
                { l: "Reviews", h: "/reviews" },
                { l: "Service Areas", h: "/#areas" },
                { l: "Contact", h: "/#contact" },
              ].map((n) => (
                <li key={n.h}>
                  <Link
                    to={n.h}
                    data-testid={`footer-link-${n.l.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {n.l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-4">
              Contact
            </div>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={BUSINESS.phoneHref}
                  className="inline-flex items-center gap-2 text-white/80 hover:text-[#E60000]"
                >
                  <Phone className="w-3.5 h-3.5" /> {BUSINESS.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BUSINESS.email}`}
                  className="inline-flex items-center gap-2 text-white/80 hover:text-[#E60000]"
                >
                  <Mail className="w-3.5 h-3.5" /> {BUSINESS.email}
                </a>
              </li>
              <li className="text-white/60">{BUSINESS.address}</li>
              <li className="text-white/60">{BUSINESS.hours}</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-10 border-t border-[#262626] grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-4">
              EV Charger Installation
            </div>
            <p className="text-sm text-white/60 leading-relaxed max-w-sm">
              Level 2 EV charger installs across the Lower Mainland — permit handled, panel checked, rebates guided.
            </p>
            <Link
              to="/guides/bc-ev-charger-rebates-2026"
              className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#E60000] hover:text-white transition-colors"
            >
              2026 BC Rebates Guide →
            </Link>
            <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              {CITY_ORDER.map((s) => (
                <Link
                  key={s}
                  to={`/services/ev-charger-installation/${s}`}
                  data-testid={`footer-city-${s}`}
                  className="text-white/60 hover:text-[#E60000] transition-colors"
                >
                  {CITIES[s].city}
                </Link>
              ))}
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-4">
              Panel Upgrade
            </div>
            <p className="text-sm text-white/60 leading-relaxed max-w-sm">
              200-amp panel upgrades and fuse box replacement — permitted, inspected, and closed with a passed BC Safety Authority sign-off.
            </p>
            <Link
              to="/guides/electrical-panel-upgrade-cost-bc-2026"
              className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#E60000] hover:text-white transition-colors"
            >
              2026 Panel Upgrade Cost Guide →
            </Link>
            <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              {PANEL_CITY_ORDER.map((s) => (
                <Link
                  key={s}
                  to={`/services/panel-upgrades/${s}`}
                  data-testid={`footer-panel-city-${s}`}
                  className="text-white/60 hover:text-[#E60000] transition-colors"
                >
                  {PANEL_CITIES[s].city}
                </Link>
              ))}
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-4">
              More Services
            </div>
            <p className="text-sm text-white/60 leading-relaxed max-w-sm">
              Commercial wiring, general electrical, lighting design, and 24/7 emergency response — all across Metro Vancouver.
            </p>
          </div>
        </div>

        {/* Affiliations & Certifications */}
        <div
          data-testid="footer-affiliations"
          className="mt-16 pt-10 border-t border-[#262626]"
        >
          <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-6">
            — Affiliations &amp; Certifications
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6 items-stretch max-w-3xl">
            <a
              href="https://members.havan.ca/directory/Details/electromax-systems-ltd-4967113"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="affiliation-havan"
              aria-label="Homebuilders Association Vancouver (HAVAN)"
              className="group flex items-center justify-center border border-[#262626] hover:border-[#E60000] transition-colors px-6 py-6 min-h-[92px]"
              title="Homebuilders Association Vancouver"
            >
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_voltage-pro-6/artifacts/5mu0c1e5_havan-logo-white.png"
                alt="Homebuilders Association Vancouver (HAVAN) — ElectroMax member affiliation"
                loading="lazy"
                width="200"
                height="72"
                className="max-h-14 md:max-h-16 w-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity"
              />
            </a>
            <a
              href={"https://betterhomesbc.ca/get-support/contractors/?tool=contractors&company=electromax"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="affiliation-cleanbc"
              aria-label="CleanBC Program approved installer"
              className="group flex items-center justify-center bg-white border border-[#262626] hover:border-[#E60000] transition-colors px-6 py-6 min-h-[92px]"
              title="CleanBC Program approved installer"
            >
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_voltage-pro-6/artifacts/q20l9nfz_logo-cleanbc-300x108-1.png"
                alt="CleanBC — ElectroMax approved rebate program installer"
                loading="lazy"
                width="240"
                height="86"
                className="max-h-12 md:max-h-14 w-auto object-contain"
              />
            </a>
            <a
              href="https://www.technicalsafetybc.ca/regulatory-resources/find-a-licensed-contractor/contractor?id=8DC66FD0-6B4D-F111-BEC7-7CED8DA65000"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="affiliation-tsbc"
              aria-label="Technical Safety BC licensed electrical contractor"
              className="group flex items-center justify-center bg-white border border-[#262626] hover:border-[#E60000] transition-colors px-6 py-6 min-h-[92px] col-span-2 sm:col-span-1 overflow-hidden"
              title="Technical Safety BC licensed"
            >
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_voltage-pro-6/artifacts/uxr57wba_tsbc.png"
                alt="Technical Safety BC — licensed electrical contractor"
                loading="lazy"
                width="240"
                height="120"
                className="max-h-20 md:max-h-24 w-auto object-contain scale-125 md:scale-[1.35]"
              />
            </a>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-[#262626] flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="text-xs text-white/40">
            © {year} ElectroMax Electrical Ltd. All rights reserved.
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/privacy-policy" data-testid="footer-link-privacy" className="text-xs text-white/40 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" data-testid="footer-link-terms" className="text-xs text-white/40 hover:text-white transition-colors">
              Terms of Use
            </Link>
            <Link to="/cookie-policy" data-testid="footer-link-cookies" className="text-xs text-white/40 hover:text-white transition-colors">
              Cookie Policy
            </Link>
          </div>
          <div className="text-xs text-white/40">
            Website &amp; marketing by{" "}
            <a
              href="https://ayvant.ca"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-ayvant-credit"
              className="text-white/70 hover:text-[#E60000] font-semibold transition-colors underline-offset-4 hover:underline"
            >
              Ayvant
            </a>
          </div>
        </div>
        <div className="mt-3 text-xs text-white/40">
          Vancouver · Surrey · Burnaby · Richmond · Langley
        </div>

        <div
          aria-hidden
          className="mt-16 overflow-hidden font-display font-black tracking-tighter text-[18vw] leading-[0.85] text-white/[0.04] select-none whitespace-nowrap"
        >
          ELECTROMAX
        </div>
      </div>
    </footer>
  );
}
